import { InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";
import { msalInstance } from "../index.js";
import { store } from "../redux/store.js";
import { setAuthSuccess } from "../redux/auth/authSlice.js";

const SCOPES = [ "api://f16a37fc-a521-4801-8610-62b5fe4ef38a/access_via_approle_assignments" ];

// Le refresh token d'une SPA Entra expire au bout de 24h sans prolongation possible, et le
// renouvellement par iframe cachée est bloqué par les navigateurs (cookies tiers). Entra impose
// donc un passage en frame "top-level" toutes les 24h. Une popup remplit cette condition sans
// détruire l'état de l'application, contrairement à acquireTokenRedirect qui recharge la page.
// Voir https://learn.microsoft.com/entra/identity-platform/refresh-tokens

// Borne l'attente d'un renouvellement manuel : sans elle, un appel API resterait
// suspendu indéfiniment si l'utilisateur ignore le bandeau.
const RENEWAL_TIMEOUT_MS = 90000;
const RETRY_DELAY_MS = 400;

export class SessionExpiredError extends Error {
    constructor(message = "Session Microsoft expirée — renouvellement requis") {
        super(message);
        this.name = "SessionExpiredError";
    }
}

let initPromise = null;
let silentInflight = null;      // dédoublonne les renouvellements silencieux concurrents
let interactiveInflight = null; // garantit une seule popup pour N appels simultanés
let pendingRenewal = null;      // promesse résolue quand l'utilisateur renouvelle via le bandeau
let notifyExpired = null;       // callback branché par SessionGuard pour afficher le bandeau

// Branché par SessionGuard : permet de demander un renouvellement à l'utilisateur
// sans recharger la page.
export function setSessionExpiredHandler(handler) {
    notifyExpired = handler;
}

// msal-browser v3 exige initialize() avant toute acquisition. MsalProvider s'en charge,
// mais un appel API parti tôt peut gagner la course et échouer sur
// `uninitialized_public_client_application`. L'appel est idempotent.
function ensureInitialized() {
    if (!initPromise) {
        initPromise = msalInstance.initialize().catch((error) => {
            initPromise = null; // autorise une nouvelle tentative
            throw error;
        });
    }
    return initPromise;
}

function tokenRequest() {
    return { scopes: SCOPES, account: msalInstance.getAllAccounts()[0] };
}

// Garde le token du store aligné sur celui de MSAL — les rôles affichés en sont dérivés.
function publish(response) {
    store.dispatch(setAuthSuccess(response.accessToken));
    return `Bearer ${response.accessToken}`;
}

// Seuls ces cas exigent réellement une action de l'utilisateur. Tout le reste
// (initialisation en cours, coupure réseau) est transitoire et ne doit jamais
// être traité comme une session expirée.
export function isInteractionRequiredError(error) {
    return error instanceof InteractionRequiredAuthError
        || (error instanceof BrowserAuthError && error.errorCode === "monitor_window_timeout");
}

// Renouvellement silencieux uniquement — ne déclenche jamais d'interface.
// Utilisé par SessionGuard pour sonder l'état de la session.
export async function getTokenSilent() {
    await ensureInitialized();
    if (!silentInflight) {
        silentInflight = msalInstance
            .acquireTokenSilent(tokenRequest())
            .finally(() => { silentInflight = null; });
    }
    return publish(await silentInflight);
}

// Renouvellement par popup : la page et son état sont préservés.
export async function renewSessionInteractive() {
    await ensureInitialized();
    if (!interactiveInflight) {
        interactiveInflight = msalInstance
            .acquireTokenPopup(tokenRequest())
            .finally(() => { interactiveInflight = null; });
    }
    const token = publish(await interactiveInflight);

    // Débloque les appels API mis en attente pendant l'expiration
    if (pendingRenewal) {
        pendingRenewal.resolve(token);
        pendingRenewal = null;
    }
    return token;
}

// Popup indisponible (bloquée par le navigateur ou fermée) : plutôt que de recharger la page et
// de perdre la recherche en cours, on affiche le bandeau et on met la requête en attente. Le clic
// de l'utilisateur est un geste explicite, ce qui garantit l'ouverture de la popup.
function waitForUserRenewal() {
    if (!notifyExpired) {
        return Promise.reject(new SessionExpiredError());
    }
    if (!pendingRenewal) {
        let resolve;
        let reject;
        const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
        const timer = setTimeout(() => {
            pendingRenewal = null;
            reject(new SessionExpiredError());
        }, RENEWAL_TIMEOUT_MS);
        pendingRenewal = {
            promise,
            resolve: (value) => { clearTimeout(timer); resolve(value); },
        };
    }
    notifyExpired();
    return pendingRenewal.promise;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function handleAcquisitionFailure(error) {
    if (!isInteractionRequiredError(error)) throw error;
    try {
        return await renewSessionInteractive();
    } catch {
        return waitForUserRenewal();
    }
}

export async function getToken() {
    try {
        return await getTokenSilent();
    } catch (error) {
        if (isInteractionRequiredError(error)) {
            return handleAcquisitionFailure(error);
        }
        // Erreur transitoire : une seule reprise, puis on propage l'erreur.
        // Ne jamais suspendre l'appel — c'est ce qui faisait échouer les recherches
        // silencieusement (spinner infini, aucune requête émise).
        await wait(RETRY_DELAY_MS);
        try {
            return await getTokenSilent();
        } catch (retryError) {
            return handleAcquisitionFailure(retryError);
        }
    }
}

export const hasRole = (roles, requiredPermission) => {
    if (requiredPermission.some((claim => roles.includes(claim)))) {
        return true;
    }

    return false;
}
